import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getBankById, saveBank, deleteBank } from '../services/api.service';
import Helper from '../services/helper.service';
import { useConfirm } from '../components/confirm-dialog';
import { FaChevronLeft, FaCopy, FaEye, FaEyeSlash, FaExternalLinkAlt } from 'react-icons/fa';

const defaultBank = {
    name: '',
    description: '',
    shopId: '',
    certificate: '',
    publicKey: '',
    integrityKey: '',
    ipnTargetUrl: '',
    useIpn: false,
};

const isValidUrl = (s) => {
    try {
        if (!s) return false;
        new URL(s);
        return true;
    } catch {
        return false;
    }
};

const BankEditorPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = Boolean(id && id !== 'new');

    const [bank, setBank] = useState(() => (isEdit ? null : { ...defaultBank }));
    const [revealed, setRevealed] = useState({ certificate: false, publicKey: false, integrityKey: false });
    const [saving, setSaving] = useState(false);

    const confirm = useConfirm();

    useEffect(() => {
        if (!isEdit) return;
        getBankById(id)
            .then((data) =>
                setBank({
                    ...defaultBank, // ensures missing fields exist
                    ...data,
                })
            )
            .catch(() => {
                Helper.toast("Impossible de charger la banque", "error");
                navigate('/banks');
            });
    }, [id, isEdit, navigate]);

    const handleChange = (field, value) => {
        setBank((prev) => ({ ...prev, [field]: value }));
    };

    const canSave = useMemo(() => {
        return Boolean(bank?.name?.trim()) && Boolean(bank?.shopId?.trim());
    }, [bank]);

    const copyToClipboard = async (value, label) => {
        try {
            await navigator.clipboard.writeText(value || '');
            Helper.toast(`${label} copié`, 'success');
        } catch {
            Helper.toast(`Impossible de copier ${label}`, 'error');
        }
    };

    const handleSave = async () => {
        if (!canSave) {
            Helper.toast('Veuillez renseigner au minimum Nom et Shop ID.', 'warning');
            return;
        }
        try {
            setSaving(true);
            await saveBank(bank);
            navigate('/banks');
        } catch {
            Helper.toast("Erreur lors de la sauvegarde", "error");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!bank?._id) return;
        const ok = await confirm({
            title: "Attention !",
            message: <>Supprimer la banque <strong>{bank.name || '(Sans titre)'}</strong> ?</>,
            confirmText: "Oui",
            cancelText: "Non",
            tone: "warning",
        });
        if (!ok) return Helper.toast("Action annulée", "info");
        try {
            await deleteBank(bank._id);
            navigate('/banks');
        } catch {
            Helper.toast("Erreur lors de la suppression", "error");
        }
    };

    if (!bank) return <div className="p-6 text-center">Chargement...</div>;

    return (
        <div className="max-w-5xl mx-auto p-6">
            {/* Sticky Header */}
            <div className="sticky top-0 z-10 mb-6">
                <div className="flex justify-between items-center py-3">
                    <button
                        onClick={() => navigate('/banks')}
                        className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
                    >
                        <FaChevronLeft className="h-4 w-4" />
                        Retour
                    </button>

                    <div className="flex gap-2">
                        {isEdit && (
                            <button
                                onClick={handleDelete}
                                className="px-4 py-2 rounded border border-red-200 text-red-700 hover:bg-red-50"
                            >
                                Supprimer
                            </button>
                        )}
                        <button
                            onClick={handleSave}
                            disabled={!canSave || saving}
                            className={`px-4 py-2 rounded text-white shadow ${!canSave || saving ? 'bg-gray-300 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
                                }`}
                        >
                            {saving ? 'Enregistrement…' : 'Enregistrer'}
                        </button>
                    </div>
                </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-800 mb-4">Édition de la banque</h2>

            {/* Card: Content */}
            <div className="rounded-2xl border bg-white shadow-sm p-5 mb-6">
                <div className="mb-4">
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                        Nom <span className="text-red-500">*</span>
                    </label>
                    <input
                        className="w-full border rounded px-3 py-2 shadow-sm"
                        placeholder="Nom de la banque"
                        value={bank.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                    />
                    {!bank.name?.trim() && (
                        <p className="mt-1 text-xs text-red-600">Le nom est requis.</p>
                    )}
                </div>

                <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                        rows={5}
                        className="w-full border rounded px-3 py-2 shadow-sm resize-y"
                        placeholder="Description de la banque (facultatif)"
                        value={bank.description}
                        onChange={(e) => handleChange('description', e.target.value)}
                    />
                </div>
            </div>

            {/* Card: Technical settings */}
            <div className="rounded-2xl border bg-white shadow-sm p-5">
                <h3 className="text-lg font-semibold mb-4">Paramètres techniques</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Shop ID */}
                    <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">
                            Shop ID <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            className="w-full border rounded px-3 py-2 shadow-sm font-mono"
                            value={bank.shopId}
                            onChange={(e) => handleChange('shopId', e.target.value)}
                        />
                        {!bank.shopId?.trim() && (
                            <p className="mt-1 text-xs text-red-600">Shop ID requis.</p>
                        )}
                    </div>

                    {/* IPN URL + toggle */}
                    <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">URL de notification IPN</label>
                        <div className="flex items-stretch gap-2">
                            <input
                                type="url"
                                className={`w-full border rounded px-3 py-2 shadow-sm ${bank.ipnTargetUrl && !isValidUrl(bank.ipnTargetUrl) ? 'border-red-300' : ''
                                    }`}
                                placeholder="https://exemple.com/ipn"
                                value={bank.ipnTargetUrl}
                                onChange={(e) => handleChange('ipnTargetUrl', e.target.value)}
                            />
                            <button
                                type="button"
                                disabled={!isValidUrl(bank.ipnTargetUrl)}
                                onClick={() => window.open(bank.ipnTargetUrl, '_blank', 'noopener,noreferrer')}
                                className={`inline-flex items-center gap-2 rounded px-3 py-2 text-sm ${isValidUrl(bank.ipnTargetUrl)
                                    ? 'border border-gray-300 hover:bg-gray-100 text-gray-700'
                                    : 'border border-gray-200 text-gray-400 cursor-not-allowed'
                                    }`}
                                title="Ouvrir"
                            >
                                <FaExternalLinkAlt className="h-3.5 w-3.5" />
                                Ouvrir
                            </button>
                        </div>
                        <label className="mt-3 inline-flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={bank.useIpn}
                                onChange={(e) => handleChange('useIpn', e.target.checked)}
                                className="h-4 w-4 text-primary focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="text-sm text-gray-700">Activer IPN</span>
                        </label>
                    </div>

                    {/* Certificate */}
                    <div className="md:col-span-2">
                        <label className="mb-1 block text-sm font-medium text-gray-700">Certificat</label>
                        <div className="flex items-stretch gap-2">
                            <input
                                type={revealed.certificate ? 'text' : 'password'}
                                className="w-full border rounded px-3 py-2 shadow-sm font-mono"
                                value={bank.certificate}
                                onChange={(e) => handleChange('certificate', e.target.value)}
                            />
                            <button
                                type="button"
                                onClick={() => setRevealed((r) => ({ ...r, certificate: !r.certificate }))}
                                className="rounded border border-gray-300 px-3 py-2 text-sm hover:bg-gray-100"
                                title={revealed.certificate ? 'Masquer' : 'Afficher'}
                            >
                                {revealed.certificate ? <FaEyeSlash /> : <FaEye />}
                            </button>
                            <button
                                type="button"
                                onClick={() => copyToClipboard(bank.certificate, 'Certificat')}
                                className="rounded border border-gray-300 px-3 py-2 text-sm hover:bg-gray-100"
                                title="Copier"
                            >
                                <FaCopy />
                            </button>
                        </div>
                    </div>

                    {/* Public key */}
                    <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Clé publique</label>
                        <div className="flex items-stretch gap-2">
                            <input
                                type={revealed.publicKey ? 'text' : 'password'}
                                className="w-full border rounded px-3 py-2 shadow-sm font-mono"
                                value={bank.publicKey}
                                onChange={(e) => handleChange('publicKey', e.target.value)}
                            />
                            <button
                                type="button"
                                onClick={() => setRevealed((r) => ({ ...r, publicKey: !r.publicKey }))}
                                className="rounded border border-gray-300 px-3 py-2 text-sm hover:bg-gray-100"
                                title={revealed.publicKey ? 'Masquer' : 'Afficher'}
                            >
                                {revealed.publicKey ? <FaEyeSlash /> : <FaEye />}
                            </button>
                            <button
                                type="button"
                                onClick={() => copyToClipboard(bank.publicKey, 'Clé publique')}
                                className="rounded border border-gray-300 px-3 py-2 text-sm hover:bg-gray-100"
                                title="Copier"
                            >
                                <FaCopy />
                            </button>
                        </div>
                    </div>

                    {/* Integrity key */}
                    <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Clé d’intégrité</label>
                        <div className="flex items-stretch gap-2">
                            <input
                                type={revealed.integrityKey ? 'text' : 'password'}
                                className="w-full border rounded px-3 py-2 shadow-sm font-mono"
                                value={bank.integrityKey}
                                onChange={(e) => handleChange('integrityKey', e.target.value)}
                            />
                            <button
                                type="button"
                                onClick={() => setRevealed((r) => ({ ...r, integrityKey: !r.integrityKey }))}
                                className="rounded border border-gray-300 px-3 py-2 text-sm hover:bg-gray-100"
                                title={revealed.integrityKey ? 'Masquer' : 'Afficher'}
                            >
                                <FaEyeSlash />
                            </button>
                            <button
                                type="button"
                                onClick={() => copyToClipboard(bank.integrityKey, 'Clé d’intégrité')}
                                className="rounded border border-gray-300 px-3 py-2 text-sm hover:bg-gray-100"
                                title="Copier"
                            >
                                <FaCopy />
                            </button>
                        </div>
                    </div>
                </div>

                <p className="mt-4 text-xs text-gray-500">
                    Astuce : conservez ces clés en lieu sûr. Utilisez “Afficher” uniquement si nécessaire.
                </p>
            </div>
        </div>
    );
};

export default BankEditorPage;